<template>
  <div v-if="assignment.reference_number" class="details-wrapper">
    <div class="details-header-wrap">
      <div class="details-header">
        <object-header :header-data="objectHeader" />
        <VueHtml2pdf
          ref="DownloadComp"
          :show-layout="false"
          :paginate-elements-by-height="1400"
          pdf-format="a4"
          :manual-pagination="false"
          :enable-download="true"
          :filename="`Assignment-${assignment.reference_number}`"
        >
          <section slot="pdf-content">
            <AssignmentDetailsPdf />
          </section>
        </VueHtml2pdf>
        <TolButton
          v-if="isTesEmployee"
          :tooltip="$t('assignment.details.actions.saveAsPdf')"
          button-type="minor"
          icon-name="file-pdf"
          class="action-round"
          @click="exportAsPdf()"
        />
      </div>

      <div v-if="isTesEmployee">
        <CreatedComment :comment="assignment.created_comment" />
      </div>
    </div>
    <div v-if="assignment.reference_number" class="details-body">
      <div class="content-panel-wrapper">
        <div v-scroll-spy data-scroll-spy-id="main-tab" class="content-panel">
          <section class="details-section">
            <AssignmentDetailsForm
              v-if="isTesEmployee"
              :assignment="assignment"
              @form-data-change="onFormDataChange"
            />
            <AssignmentHeader v-else :assignment="assignment" />
          </section>
          <section>
            <tol-button
              button-type="major"
              :label="showHideTimesheets"
              :icon-name="iconFor(showHideListing['TIMESHEETS'])"
              @click="toggleListing('TIMESHEETS')"
            />
          </section>
          <section
            v-if="assignmentTimeSheets.length > 0 && showHideListing['TIMESHEETS']"
            class="details-section"
          >
            <h1>{{ $t('timeSheets.listing.title') }}</h1>
            <tol-table
              :table-data="assignmentTimeSheets"
              :columns="timeSheetsColumns"
              data-label-prefix="timeSheets"
              :show-spinner="isAssignmentTimesheetsLoading"
              :is-row-clickable="true"
              @row-click="onRowClick($event)"
            >
              <template v-slot:tableHeader>
                <th>{{ $t('timeSheets.listing.labels.modifiedBy') }}</th>

                <th-sortable
                  :direction="
                    currentSortFieldForTimeSheets === 'startDate'
                      ? currentSortDirectionForTimeSheets
                      : 'none'
                  "
                  field-name="startDate"
                  @change="onTimeSheetsSortOrderChange"
                  >{{ $t('timeSheets.listing.labels.startDate') }}</th-sortable
                >

                <th-sortable
                  :direction="
                    currentSortFieldForTimeSheets === 'endDate'
                      ? currentSortDirectionForTimeSheets
                      : 'none'
                  "
                  field-name="endDate"
                  @change="onTimeSheetsSortOrderChange"
                  >{{ $t('timeSheets.listing.labels.endDate') }}</th-sortable
                >

                <th-sortable
                  :direction="
                    currentSortFieldForTimeSheets === 'totalHours'
                      ? currentSortDirectionForTimeSheets
                      : 'none'
                  "
                  field-name="totalHours"
                  @change="onTimeSheetsSortOrderChange"
                  >{{ $t('timeSheets.listing.labels.totalHours') }}</th-sortable
                >

                <th-sortable
                  :direction="
                    currentSortFieldForTimeSheets === 'totalDays'
                      ? currentSortDirectionForTimeSheets
                      : 'none'
                  "
                  field-name="totalDays"
                  @change="onTimeSheetsSortOrderChange"
                  >{{ $t('timeSheets.listing.labels.totalDays') }}</th-sortable
                >
                <th>{{ $t('timeSheets.listing.labels.status') }}</th>
              </template>

              <template v-slot:referenceNumber="{ value }">
                <router-link :to="'/time-sheets/' + value.referenceNumber">
                  {{ value.referenceNumber }}
                </router-link>
              </template>

              <template v-slot:status="{ value }">
                <Status :status="value.status" object-type="time-sheet" />
              </template>
            </tol-table>
            <Pagination
              :records-per-page="timeSheetsPerPage"
              :offset="currentTimeSheetPage"
              :record-count="timeSheetCount"
              @change="onChangeTimeSheetsPaginationParams($event)"
            />
          </section>
          <section v-if="showHideListing['TIMESHEETS'] && assignmentTimeSheets.length === 0">
            <div class="empty">
              {{ $t('assignment.details.errors.noRecords', { record: 'time sheets' }) }}
            </div>
          </section>
          <section>
            <div v-if="canCreate" class="createtimesheet-form">
              <form>
                <section>
                  <h1>{{ $t('assignments.listing.labels.createTimesheet') }}</h1>
                  <div class="form-control">
                    <label>{{ $t('timeSheets.listing.labels.startDate') }} </label>
                    <form-date-picker
                      v-model="timesheetStartDate"
                      :class="{ error: '' }"
                      @change="onStartDateSelect"
                    >
                    </form-date-picker>
                  </div>

                  <div class="form-control">
                    <label> {{ $t('timeSheets.listing.labels.endDate') }}</label>
                    <form-date-picker
                      v-model="timesheetEndDate"
                      :class="{ error: '' }"
                      @change="onEndDateSelect"
                    >
                    </form-date-picker>
                  </div>

                  <div class="form-control">
                    <label> {{ $t('timeSheets.listing.labels.comment') }} </label>
                    <InputField
                      v-model="createdComment"
                      type="text"
                      class="form-input"
                      field-icon="comments"
                    />
                  </div>
                  <div class="form-control">
                    <TolButton
                      icon-name="plus"
                      button-type="minor"
                      class="action-round"
                      tooltip="Create Timesheet"
                      @click="createTimesheet"
                    />
                  </div>
                </section>
              </form>
            </div>
          </section>
          <section>
            <tol-button
              button-type="major"
              :label="showHideExpenses"
              :icon-name="iconFor(showHideListing['EXPENSES'])"
              @click="toggleListing('EXPENSES')"
            />
          </section>
          <section
            v-if="assignmentExpenses.length > 0 && showHideListing['EXPENSES']"
            class="details-section"
          >
            <h1>{{ $t('expenses.listing.title') }}</h1>
            <tol-table
              :table-data="assignmentExpenses"
              :columns="expensesColumns"
              data-label-prefix="expenses"
              :show-spinner="isAssignmentExpensesLoading"
            >
              <template v-slot:tableHeader>
                <th>{{ $t('expenses.listing.labels.referenceNumber') }}</th>

                <th-sortable
                  :direction="
                    currentSortFieldForExpenses === 'clientName'
                      ? currentSortDirectionForExpenses
                      : 'none'
                  "
                  field-name="clientName"
                  @change="onExpensesSortOrderChange"
                  >{{ $t('expenses.listing.labels.client') }}</th-sortable
                >
                <th-sortable
                  :direction="
                    currentSortFieldForExpenses === 'startDate'
                      ? currentSortDirectionForExpenses
                      : 'none'
                  "
                  field-name="startDate"
                  @change="onExpensesSortOrderChange"
                  >{{ $t('expenses.listing.labels.startDate') }}</th-sortable
                >

                <th-sortable
                  :direction="
                    currentSortFieldForExpenses === 'endDate'
                      ? currentSortDirectionForExpenses
                      : 'none'
                  "
                  field-name="endDate"
                  @change="onExpensesSortOrderChange"
                  >{{ $t('expenses.listing.labels.endDate') }}</th-sortable
                >

                <th-sortable
                  :direction="
                    currentSortFieldForExpenses === 'totalAmount'
                      ? currentSortDirectionForExpenses
                      : 'none'
                  "
                  field-name="totalAmount"
                  @change="onExpensesSortOrderChange"
                  >{{ '$ ' + $t('expenses.listing.labels.totalAmount') }}</th-sortable
                >

                <th>{{ $t('expenses.listing.labels.status') }}</th>
              </template>
              <template v-slot:referenceNumber="{ value }">
                <router-link :to="'/expenses/' + value.referenceNumber">
                  {{ value.referenceNumber }}
                </router-link>
              </template>
              <template v-slot:status="{ value }">
                <Status :status="value.status" object-type="expense" />
              </template>
            </tol-table>
            <Pagination
              :records-per-page="expensesPerPage"
              :offset="currentExpensePage"
              :record-count="expenseCount"
              @change="onChangeExpensesPaginationParams($event)"
            />
          </section>
          <section v-if="showHideListing['EXPENSES'] && assignmentExpenses.length === 0">
            <div class="empty">
              {{ $t('assignment.details.errors.noRecords', { record: 'expenses' }) }}
            </div>
          </section>
          <section
            v-if="canViewAttachment && assignment && assignment.reference_number"
            class="details-section"
          >
            <h1>{{ $t('assignment.labels.attachments') }}</h1>
            <div>
              <Attachment
                :reference-number="assignment.reference_number"
                owner="ASSIGNMENT"
                @attachment-success="refreshAttachments"
              />
              <tol-button
                button-type="major"
                :label="showHideAttachments"
                :icon-name="iconFor(showHideListing['ATTACHMENTS'])"
                @click="toggleListing('ATTACHMENTS')"
              />
              <AttachmentsList
                v-if="showHideListing['ATTACHMENTS']"
                attachment-type="ASSIGNMENT"
                :attachments="assignmentAttachments"
                :attachment-options="attachmentOptions"
                @delete-attachment="confirmAttachmentDelete"
                @refresh="refreshAttachments"
              />
            </div>
          </section>
          <section>
            <tol-button
              button-type="major"
              :label="showHideHistory"
              :icon-name="iconFor(showHideListing['HISTORY'])"
              @click="toggleListing('HISTORY')"
            />
          </section>
          <section
            v-if="assignmentHistories.length && isTesEmployee && showHideListing['HISTORY']"
            class="details-section"
          >
            <h1>{{ $t('assignment.labels.history') }}</h1>
            <tol-table
              :table-data="assignmentHistories"
              :columns="historiesColumns"
              :expandable="true"
              data-label-prefix="assignment.history"
            >
              <template v-slot:tableHeader>
                <th></th>
                <th>{{ $t('assignment.history.listing.labels.created_at') }}</th>
                <th>{{ $t('assignment.history.listing.labels.created_by_name') }}</th>
                <th>{{ $t('assignment.history.listing.labels.status') }}</th>
                <th>{{ $t('assignment.history.listing.labels.created_comment') }}</th>
              </template>
              <template v-slot:created_at="{ value }">
                <!--{{ value.created_at | formatDateTime }} -->
                {{ value.created_at | formatDateTime }}
              </template>
              <template v-slot:status="{ value }">
                <Status :status="value.status" object-type="assignment" />
              </template>
              <template v-slot:expanded="{ value }">
                <AssignmentDetailsForm
                  :assignment="getSelectedHistoryRecord(value.id)"
                  :is-history-record="true"
                />
              </template>
            </tol-table>
          </section>
          <section v-if="showHideListing['HISTORY'] && assignmentHistories.length === 0">
            <div class="empty">
              {{ $t('assignment.details.errors.noRecords', { record: 'history records' }) }}
            </div>
          </section>
        </div>
      </div>

      <div class="assignment-status" :class="statusClass">{{ assignment.status }}</div>
    </div>
    <div v-else class="empty">
      {{ $t('assignment.details.errors.noAssignment') }}
    </div>
    <update-modal v-if="isLoading">
      <template v-slot:modalData>
        <vue-simple-spinner size="massive" />
      </template>
    </update-modal>
    <ConfirmModal
      v-if="isDeleteAttachmentModalDisplayed"
      action="DELETE_ATTACHMENT"
      :title="$t('attachments.confirmDeleteTitle')"
      @continue-action="deleteAttachment()"
      @cancel-action="cancelDeletingAttachment()"
    >
      <template v-slot:modalMessage>
        {{ $t('attachments.confirmDelete') }}
      </template>
    </ConfirmModal>
  </div>
</template>

<script>
import AssignmentDetailsForm from './components/AssignmentDetailsForm.vue';
import AssignmentHeader from './components/AssignmentHeader.vue';
import AssignmentDetailsPdf from './AssignmentDetailsPdf';
import { AssignmentDetailsActions, AssignmentDetailsGetters } from './assignment-details.keys';
import { attachmentService } from '@/app/components/attachments/attachment.service';
import ConfirmModal from '@/app/components/ConfirmModal';
import TolTable from '@/app/components/TolTable';
import TolButton from '@/app/components/TolButton';
import UpdateModal from '@/app/components/UpdateModal';
import { AppActions } from '@/app/app.keys';
import Status from '@/app/components/Status';
import ThSortable from '@/app/components/ThSortable';
import FormDatePicker from '@/app/components/FormDatePicker';
import format from 'date-fns/format';
import InputField from '@/app/components/InputField';
import AttachmentsList from '@/app/components/attachments/AttachmentsList';
import Attachment from '@/app/components/attachments/Attachment';
import { dateUtils } from '@/mixins/date-utils';
import CreatedComment from '@/app/components/CreatedComment';
import ObjectHeader from '@/app/components/ObjectHeader';
import formatDateTime from '@/filters/format-datetime.filter.js';
import Pagination from '@/app/components/Pagination';

const ASSIGNMENT_DETAIL_ITEMS = ['TIMESHEETS', 'EXPENSES', 'ATTACHMENTS', 'HISTORY'];

export default {
  name: 'AssignmentDetails',
  components: {
    AssignmentDetailsForm,
    AssignmentHeader,
    AssignmentDetailsPdf,
    ConfirmModal,
    TolTable,
    Status,
    ThSortable,
    TolButton,
    FormDatePicker,
    InputField,
    UpdateModal,
    AttachmentsList,
    Attachment,
    CreatedComment,
    ObjectHeader,
    Pagination
  },
  mixins: [dateUtils],
  data() {
    return {
      showTimesheetList: false,
      showHideListing: {
        TIMESHEETS: false,
        EXPENSES: false,
        ATTACHMENTS: false,
        HISTORY: false
      },
      isDeleteAttachmentModalDisplayed: false,
      selectedAttachmentId: null,
      tmsStartDate: '',
      createdComment: '',
      tmsEndDate: '',
      timeSheetsColumns: [
        'lastModified',
        'startDate',
        'endDate',
        'totalHours',
        'totalDays',
        'status'
      ],
      expensesColumns: [
        'referenceNumber',
        'clientName',
        'startDate',
        'endDate',
        'totalAmount',
        'status'
      ],
      historiesColumns: ['created_at', 'created_by_name', 'status', 'created_comment'],
      sidebarMenu: [
        {
          label: this.$t('assignment.labels.details'),
          submenu: [
            { label: this.$t('assignment.labels.contractorDetails') },
            { label: this.$t('assignment.labels.clientDetails') },
            { label: this.$t('assignment.labels.stakeholdersDetails') },
            { label: this.$t('assignment.labels.miscDetails') }
          ]
        },
        { label: this.$t('assignment.labels.timeSheets') },
        { label: this.$t('assignment.labels.expenses') },
        { label: this.$t('assignment.labels.attachments') },
        { label: this.$t('assignment.labels.history') }
      ],
      attachmentOptions: [
        'Schedule F',
        'Buyer Approval Email',
        'Employment Exemption Email',
        'Education Exemption Email',
        'Code of Conduct Acknowledgement',
        'Client IT Asset Security Policy Sign-Off',
        'SOW',
        'Work Assignment / Work Authorization',
        'RFR Document',
        'Contract',
        'Signed Contract Pages',
        'Extension Letter',
        'Non-Disclosure Agreement',
        'Purchase Order',
        'Equipment & Property Loan Agreement',
        'Adjustment Letter',
        'Addendum Letter',
        'Schedules A,B,C,D',
        'Billing Info',
        'Vacation Pay Agreement',
        'Agreement for Excess Hours',
        'Annex A - CUSA',
        'Background Screening Cost Acknowledgement',
        'IBM Audit Consent Form',
        'Payment Option',
        'Individual Contractor Letter',
        'Other'
      ]
    };
  },
  computed: {
    showHideHistory() {
      return this.showHideListing['HISTORY']
        ? this.$t('assignments.listing.labels.hideHistory')
        : this.$t('assignments.listing.labels.showHistory');
    },
    showHideAttachments() {
      return this.showHideListing['ATTACHMENTS']
        ? this.$t('assignments.listing.labels.hideAttachments')
        : this.$t('assignments.listing.labels.showAttachments');
    },
    iconFor() {
      return showHide => (showHide ? 'eye' : 'eye-slash');
    },
    showHideTimesheets() {
      return this.showHideListing['TIMESHEETS']
        ? this.$t('assignments.listing.labels.hideTimeSheets')
        : this.$t('assignments.listing.labels.showTimeSheets');
    },
    showHideExpenses() {
      return this.showHideListing['EXPENSES']
        ? this.$t('assignments.listing.labels.hideExpenses')
        : this.$t('assignments.listing.labels.showExpenses');
    },
    isTesEmployee() {
      return (
        this.currentUser &&
        this.currentUser.roles.map(r => r.role_name).some(role => role === 'TES Employee')
      );
    },
    currentUser() {
      return this.$store.state.currentUser;
    },
    isLoading() {
      return this.$store.getters[AssignmentDetailsGetters.IsAssignmentDetailsLoading];
    },
    isAssignmentTimesheetsLoading() {
      return this.$store.getters[AssignmentDetailsGetters.IsAssignmentTimesheetsLoading];
    },
    isAssignmentExpensesLoading() {
      return this.$store.getters[AssignmentDetailsGetters.IsAssignmentExpensesLoading];
    },
    statusClass() {
      let className = '';
      switch (this.assignment.status) {
        case 'DRAFT':
          className = 'draft';
          break;
        case 'ACTIVE_ONLINE':
          className = 'active-online';
          break;
        case 'ACTIVE_OFFLINE':
          className = 'active-offline';
          break;
        case 'SUBMITTED':
          className = 'submitted';
          break;
        case 'APPROVED':
          className = 'approved';
          break;
        case 'REJECTED':
          className = 'rejected';
          break;
        case 'OBSOLETE':
          className = 'obsolete';
          break;
        case 'FINISHED':
          className = 'finished';
          break;
      }
      return className;
    },
    assignment: {
      get() {
        const assignment = this.$store.getters[AssignmentDetailsGetters.CurrentAssignment];
        assignment.purchase_order_effective_date = null;
        return assignment;
      }
    },
    canCreate() {
      return (
        !!this.currentUser &&
        !!this.currentUser.permissions['TIME_SHEET'] &&
        !!this.currentUser.permissions['TIME_SHEET']['CREATE']
      );
    },
    assignmentTimeSheets: {
      get() {
        return this.$store.getters[AssignmentDetailsGetters.CurrentAssignmentTimeSheets];
      }
    },
    assignmentExpenses() {
      return this.$store.getters[AssignmentDetailsGetters.CurrentAssignmentExpenses];
    },
    assignmentHistories: {
      get() {
        return this.$store.getters[AssignmentDetailsGetters.CurrentAssignmentHistories];
      }
    },
    assignmentAttachments() {
      return this.$store.getters[AssignmentDetailsGetters.CurrentAssignmentAttachments];
    },
    timeSheetsPerPage() {
      return this.$store.getters[AssignmentDetailsGetters.AssignmentTimeSheetsPerPage];
    },
    currentTimeSheetPage() {
      return this.$store.getters[AssignmentDetailsGetters.CurrentAssignmentTimeSheetsPage];
    },
    timeSheetCount() {
      return this.$store.getters[AssignmentDetailsGetters.AssignmentTimeSheetCount];
    },
    expensesPerPage() {
      return this.$store.getters[AssignmentDetailsGetters.AssignmentExpensesPerPage];
    },
    currentExpensePage() {
      return this.$store.getters[AssignmentDetailsGetters.CurrentAssignmentExpensesPage];
    },
    expenseCount() {
      return this.$store.getters[AssignmentDetailsGetters.AssignmentExpenseCount];
    },
    currentSortFieldForTimeSheets() {
      return this.$store.getters[AssignmentDetailsGetters.AssignmentTimeSheetsSortOrder].sortBy;
    },
    currentSortDirectionForTimeSheets() {
      return this.$store.getters[AssignmentDetailsGetters.AssignmentTimeSheetsSortOrder]
        .sortDirection;
    },
    currentSortFieldForExpenses() {
      return this.$store.getters[AssignmentDetailsGetters.AssignmentExpensesSortOrder].sortBy;
    },
    currentSortDirectionForExpenses() {
      return this.$store.getters[AssignmentDetailsGetters.AssignmentExpensesSortOrder]
        .sortDirection;
    },
    timesheetStartDate() {
      return this.tmsStartDate ? this.dateFromString(this.tmsStartDate) : null;
    },
    timesheetEndDate() {
      return this.tmsEndDate ? this.dateFromString(this.tmsEndDate) : null;
    },
    canViewAttachment() {
      return (
        this.currentUser &&
        this.currentUser.permissions &&
        this.currentUser.permissions['ASSIGNMENT']['VIEW_ATTACHMENT']
      );
    },
    objectHeader() {
      const data = {
        referenceNumber: this.assignment.reference_number,
        objectStatus: this.assignment.status,
        objectType: 'ASSIGNMENT',
        lastModified: null
      };
      /*const lastModified = `${this.assignment.lastModified.first_name} ${
        this.assignment.lastModified.last_name
      } at ${this.assignment.created_at | formatDateTime}`;*/

      const lastModified = `${this.assignment.lastModified.first_name} ${
        this.assignment.lastModified.last_name
      } at ${formatDateTime(this.assignment.created_at)}`;

      if (this.isTesEmployee) {
        data.lastModified = lastModified;
      }

      return data;
    }
  },
  watch: {
    $route: {
      immediate: true,
      async handler(val) {
        await this.fetchAssignment(val.params.id);
        ASSIGNMENT_DETAIL_ITEMS.forEach(item => {
          if (this.showHideListing[item]) {
            this.$store.dispatch(AssignmentDetailsActions.RetrieveIfNotLoaded, {
              item,
              referenceNumber: val.params.id
            });
          }
        });
      }
    }
  },
  mounted() {
    this.$store.dispatch(AppActions.SetPageTitle, this.$t('assignment.details.title'));
    this.fetchAssignment(this.$route.params.id);
  },
  methods: {
    onRowClick(evt) {
      this.$router.push(`/time-sheets/${evt.referenceNumber}`);
    },
    toggleListing(item) {
      this.showHideListing[item] = !this.showHideListing[item];
      if (this.showHideListing[item]) {
        this.$store.dispatch(AssignmentDetailsActions.RetrieveIfNotLoaded, {
          item,
          referenceNumber: this.assignment.reference_number
        });
      }
    },
    confirmAttachmentDelete(attachmentId) {
      this.selectedAttachmentId = attachmentId;
      this.isDeleteAttachmentModalDisplayed = true;
    },
    async deleteAttachment() {
      await attachmentService.deleteAttachment(this.selectedAttachmentId);
      this.isDeleteAttachmentModalDisplayed = false;
      this.selectedAttachmentId = null;
      this.refreshAttachments();
    },
    cancelDeletingAttachment() {
      this.isDeleteAttachmentModalDisplayed = false;
      this.selectedAttachmentId = null;
    },
    onChangeTimeSheetsPaginationParams(evt) {
      this.$store.dispatch(AssignmentDetailsActions.SetAssignmentTimeSheetsPaginationParams, {
        event: evt,
        referenceNumber: this.assignment.reference_number
      });
    },
    onChangeExpensesPaginationParams(evt) {
      this.$store.dispatch(AssignmentDetailsActions.SetAssignmentExpensesPaginationParams, {
        event: evt,
        referenceNumber: this.assignment.reference_number
      });
    },
    fetchAssignment(ref) {
      this.$store.dispatch(AssignmentDetailsActions.RetrieveAssignment, ref);
    },
    exportAsPdf() {
      this.$refs.DownloadComp.generatePdf();
    },
    getSelectedHistoryRecord(assignmentId) {
      return this.$store.getters[AssignmentDetailsGetters.SelectedHistoryRecord](assignmentId);
    },
    formatDate(date) {
      return date ? format(date, 'YYYY-MM-DD') : null;
    },
    onFormDataChange() {
      this.isOnScreenDataModified = true;
    },
    onTimeSheetsSortOrderChange(sortOrder) {
      this.$store.dispatch(AssignmentDetailsActions.SetAssignmentTimeSheetsSortOrder, sortOrder);
    },
    onExpensesSortOrderChange(sortOrder) {
      this.$store.dispatch(AssignmentDetailsActions.SetAssignmentExpensesSortOrder, sortOrder);
    },
    onStartDateSelect(event) {
      this.tmsStartDate = this.formatDate(event);
    },
    onEndDateSelect(event) {
      this.tmsEndDate = this.formatDate(event);
    },
    createTimesheet() {
      let timesheetFields = {
        startDate: this.tmsStartDate,
        endDate: this.tmsEndDate,
        referenceNumber: this.assignment.reference_number,
        comment: this.createdComment
      };
      this.$store.dispatch(AssignmentDetailsActions.CreateNewTimesheet, timesheetFields);
    },
    refreshAttachments() {
      this.$store.dispatch(
        AssignmentDetailsActions.RetrieveAssignmentAttachments,
        this.$route.params.id
      );
    }
  }
};
</script>

<style scoped lang="scss">
@import '@/app/styles/colors.scss';
@import '@/app/styles/variables.scss';

.details-header-wrap {
  display: flex;
  flex-direction: column;
}

.blue-comment {
  border: 3px solid red;
  background-color: #d9edf7 !important;
  background: #d9edf7 !important;
  width: fit-content;
}
.details-wrapper {
  display: flex;
  flex-direction: column;

  background-color: $background-level-2;
  .details-header {
    display: flex;

    align-items: center;
    padding: 0 $details-padding;
    border-bottom: 2px solid $row-border;
    justify-content: space-between;
    .object-header {
      border-bottom: 0;
    }
  }
  .details-body {
    position: relative;
    display: flex;
    flex-direction: row;
    justify-content: center;
    border-right: 4px solid rgba(255, 255, 255, 0);
    &.modified {
      border-right: 4px solid $modified-form;
    }

    .content-panel-wrapper {
      flex: 0 85%;
      .content-panel {
        display: block;
        background-color: $white;
        padding: 60px 0;
      }
    }

    .details-section {
      padding-bottom: 25px;

      h1 {
        border-bottom: 1px solid $row-border;
      }
    }

    .assignment-status {
      position: fixed;
      width: 170px;
      padding: 10px 0;
      transform: rotate(-90deg);
      top: 50%;
      right: 0;
      color: $white;
      font-weight: bold;
      text-align: center;
      &.draft {
        background-color: $light-grey-1;
        border: 1px solid $dark-grey-1;
      }
      &.submitted {
        background-color: $orange;
        border: 1px solid $secondary-orange;
      }
      &.active-online {
        background-color: $secondary-green;
        border: 1px solid $green;
      }
      &.finished,
      &.approved {
        background-color: $blue;
        border: 1px solid $dark-blue;
      }
      &.rejected {
        background-color: $red;
        border: 1px solid $secondary-red;
      }
      &.obsolete {
        background-color: $red;
        border: 1px solid $secondary-red;
      }
      &.active-offline {
        background-color: $purple;
        border: 1px solid $purple;
      }
    }
  }

  .empty {
    display: flex;
    padding: 60px;
  }

  .createtimesheet-form {
    form {
      width: 100%;
      section {
        margin-bottom: 20px;
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;

        h1 {
          padding-top: 15px;
          border-bottom: 1px solid $row-border;
        }
        h1,
        h2 {
          flex: 0 100%;
        }
        .form-control {
          padding: 10px 0;
          margin: 1em;
          flex: 1;
          > label {
            display: block;
            font-weight: bold;
          }
        }
      }
    }
  }
}
@media print {
  .details-wrapper {
    .createtimesheet-form {
      display: none;
    }
  }
  .details-wrapper {
    .createtimesheet-form {
      display: none;
    }
  }
}
</style>
